/* eslint-disable react/no-unknown-property */
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import {
  Html,
  OrbitControls,
  PerspectiveCamera,
  Plane,
  useContextBridge,
  useProgress,
} from '@react-three/drei';
import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import Style from './style.module.scss';
import Marker from 'componentsForThree/Marker';
import { ControlContext } from 'provider/ControlProvider';
import Popup from 'components/Popup';
import controlBtn from 'assets/img/back.png';
import logo from 'assets/img/pro2.png';
import ToggleButton from 'components/ToggleButton';
import MarkerParent from 'componentsForThree/MarkerParent';
import { PuffLoader } from 'react-spinners';
import Model from 'componentsForThree/ModelLoader';
import { DoubleSide } from 'three';
import breast from 'assets/img/icon_breast.png';
import colon from 'assets/img/icon_colon.png';
import liver from 'assets/img/icon_liver.png';
import lung from 'assets/img/icon_lung.png';
import nasopharyngeal from 'assets/img/icon_nasopharyngeal.png';
import prostate from 'assets/img/icon-prostate.png';

type MarkerInfo = {
  name: string;
  pos: Array<number>;
};
type ButtonInfo = {
  name: string;
  id: number;
  zone: number;
  img: string;
  pos: Array<number>;
};
type CameraProps = {
  pos: Array<number>;
};

const zoneA = new Array<MarkerInfo>(
  {
    name: 'Brain Tumour',
    pos: [0, 17.7, 0],
  },
  {
    name: 'Tonsil Cancer',
    pos: [-0.15, 16.25, 0],
  },
  {
    name: 'Throat Cancer',
    pos: [-0.42, 15.83, 0],
  },
  {
    name: 'Thyroid Cancer',
    pos: [-0.59, 14.85, 0],
  },
  {
    name: 'Lymphoma',
    pos: [-0.59, 15.26, 0.57],
  },
  {
    name: 'Nasopharyngeal Cancer',
    pos: [0.17, 16.446, -0.166],
  }
);
const zoneB = new Array<MarkerInfo>(
  {
    name: 'Lung Cancer',
    pos: [0.038, 14, 0.678],
  },
  {
    name: 'Skin Cancer',
    pos: [-1.08, 15.3, -1.33],
  },
  {
    name: 'Leukaemia',
    pos: [-0.882, 14.473, 3.24],
  },
  {
    name: 'Oesophageal Cancer',
    pos: [-0.456, 14.83, -0.055],
  },
  {
    name: 'Stomach Cancer',
    pos: [0.417, 11.7, -0.157],
  },
  {
    name: 'Liver Cancer',
    pos: [0.417, 12.012, 0.788],
  },
  {
    name: 'Breast Cancer',
    pos: [0.7, 14, -0.8],
  }
);
const zoneBF = new Array<MarkerInfo>(
  {
    name: 'Lung Cancer',
    pos: [0.038, 14, 0.678],
  },
  {
    name: 'Skin Cancer',
    pos: [-1.08, 15.3, -1.33],
  },
  {
    name: 'Leukaemia',
    pos: [-0.882, 14.473, 3.24],
  },
  {
    name: 'Oesophageal Cancer',
    pos: [-0.456, 14.83, -0.055],
  },
  {
    name: 'Stomach Cancer',
    pos: [0.417, 11.7, -0.157],
  },
  {
    name: 'Liver Cancer',
    pos: [0.417, 12.012, 0.788],
  },
  {
    name: 'Breast Cancer',
    pos: [1.108, 13.186, -0.8],
  }
);
const zoneC = new Array<MarkerInfo>(
  {
    name: 'Bone Cancer',
    pos: [-0.154, 9.982, 1.186],
  },
  {
    name: 'Pancreatic Cancer',
    pos: [0.417, 11.8, 0.43],
  },
  {
    name: 'Kidney Cancer',
    pos: [-0.814, 11.572, -0.503],
  },
  {
    name: 'Colorectal Cancer',
    pos: [0.092, 10.877, 1.062],
  }
);
const zoneD = new Array<MarkerInfo>(
  {
    name: 'Prostate Cancer',
    pos: [0.092, 9.31, -0.011],
  },
  {
    name: 'Testicular Cancer',
    pos: [0.092, 8.026, -0.358],
  }
);
const zoneCF = new Array<MarkerInfo>(
  {
    name: 'Bone Cancer',
    pos: [-0.154, 9.982, 1.186],
  },
  {
    name: 'Pancreatic Cancer',
    pos: [0.417, 11.8, 0.43],
  },
  {
    name: 'Kidney Cancer',
    pos: [-0.814, 11.572, -0.503],
  },
  {
    name: 'Colorectal Cancer',
    pos: [0.092, 10.877, 1.062],
  }
);

const zoneDF = new Array<MarkerInfo>(
  {
    name: 'Ovarian Cancer',
    pos: [0.125, 9.728, -0.635],
  },
  {
    name: 'Cervical Cancer',
    pos: [0.125, 8.826, -0.118],
  }
);

const buttonListM = new Array<ButtonInfo>(
  {
    name: 'Prostate',
    id: 17,
    img: prostate,
    zone: 3,
    pos: [0.092, 9.31, -0.011],
  },
  {
    name: 'Colon',
    id: 16,
    img: colon,
    zone: 2,
    pos: [0.092, 10.877, 1.062],
  },
  {
    name: 'Lung',
    id: 6,
    img: lung,
    zone: 1,
    pos: [0.038, 14, 0.678],
  },
  {
    name: 'Nasopharyngeal',
    id: 5,
    img: nasopharyngeal,
    zone: 0,
    pos: [0.17, 16.446, -0.166],
  },
  {
    name: 'Liver',
    id: 11,
    img: liver,
    zone: 1,
    pos: [0.417, 12.012, 0.788],
  }
);

const buttonListF = new Array<ButtonInfo>(
  {
    name: 'Breast',
    id: 12,
    img: breast,
    zone: 1,
    pos: [0.7, 14, -0.8],
  },
  {
    name: 'Colon',
    zone: 2,
    id: 16,
    img: colon,
    pos: [0.092, 10.877, 1.062],
  },
  {
    name: 'Lung',
    zone: 1,
    id: 6,
    img: lung,
    pos: [0.038, 14, 0.678],
  },
  {
    name: 'Nasopharyngeal',
    id: 5,
    img: nasopharyngeal,
    zone: 0,
    pos: [0.17, 16.446, -0.166],
  },
  {
    name: 'Liver',
    id: 11,
    img: liver,
    zone: 1,
    pos: [0.417, 12.012, 0.788],
  }
);
export default function Configurator() {
  const ContextBridge = useContextBridge(ControlContext);
  const { active, progress } = useProgress();
  const {
    buttonIndex,
    popupRef,
    gender,
    percentage,
    setGender,
    setButtonIndex,
  } = useContext(ControlContext);
  const [sindex, setIndex] = useState<number>(-1);
  const [markVisible, SetMarkVisible] = useState<boolean>(false);
  const orbit = useRef<any>();
  const camera = useRef<any>();
  const myMesh = useRef<THREE.Mesh>();
  const [zoneIndex, setZoneIndex] = useState<number>(-1);
  const [drag, setDrag] = useState<boolean>(false);
  useEffect(() => {
    if (progress === 100 && !active) {
      if (drag)
        orbit.current.touches = {
          ONE: THREE.TOUCH.PAN,
          TWO: THREE.TOUCH.DOLLY_PAN,
        };
      else
        orbit.current.touches = {
          ONE: THREE.TOUCH.ROTATE,
          TWO: THREE.TOUCH.DOLLY_PAN,
        };
    }
  }, [drag]);
  const cameraMove = ({ pos }: CameraProps) => {
    gsap.to(orbit.current.target, {
      duration: 1,
      ease: 'back.out(0.8)',
      x: pos[0],
      y: pos[1],
      z: pos[2],
    });
    gsap.to(orbit.current, {
      onStart: () => {
        orbit.current.minDistance = 7;
      },
      duration: 1.2,
      ease: 'power1.out',
      maxDistance: 15,
    });
    gsap.to('#resetBtn', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
    });
  };
  const cameraRest = () => {
    setIndex(-1);
    gsap.to(orbit.current.target, {
      duration: 1,
      ease: 'back.out(0.8)',
      x: 0,
      y: 12,
      z: 0,
    });

    gsap.to(orbit.current, {
      onStart: () => {
        orbit.current.maxDistance = 80;
      },
      duration: 1.2,
      ease: 'power1.out',
      minDistance: 50,
      onComplete: () => {
        orbit.current.minDistance = 7;
      },
    });
    setZoneIndex(-1);
    canvaseMoveRight();
    popupInitAnim();
  };
  const canvaseMoveLeft = () => {
    if (window.innerWidth >= 1024) {
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: -popupRef.current.offsetWidth + 200,
      });
      gsap.to('#resetBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: -200,
      });
      gsap.to('#controlBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: -200,
      });
    } else
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        y: -200,
        onComplete: () => {
          gsap.to('#controlBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: 200,
          });
          gsap.to('#resetBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: -250,
          });
        },
      });
  };
  const canvaseMoveRight = () => {
    if (window.innerWidth >= 1024) {
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: 0,
      });
      gsap.to('#resetBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: 0,
      });
      gsap.to('#controlBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: 0,
      });
    } else
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        y: 0,
        onComplete: () => {
          gsap.to('#controlBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: 0,
          });
          gsap.to('#resetBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: 0,
          });
        },
      });
  };
  const markAnim = () => {
    gsap.to('#marker', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 0,
    });
    gsap.to('#plusMark', {
      duration: 0.3,
      ease: 'power4.Out',
      fontSize: 1,
    });
  };
  const markInitAnim = () => {
    gsap.to('#marker', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
    });
    gsap.to('#plusMark', {
      duration: 0.3,
      ease: 'power4.Out',
      fontSize: 20,
    });
  };
  const popupAnim = (index: number) => {
    gsap.to('#popup', {
      onStart: () => {
        setButtonIndex(index);
      },
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
      onComplete: () => {
        canvaseMoveLeft();
      },
    });
    gsap.to('#resetBtn', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
    });
    gsap.fromTo(
      '#textInfo',
      { duration: 1, ease: 'power1.out', opacity: 0 },
      { duration: 1, ease: 'power1.out', opacity: 1 }
    );
  };
  const popupInitAnim = () => {
    gsap.to('#popup', {
      duration: 1,
      ease: 'power1.out',
      opacity: 0,
      onComplete: () => {
        setButtonIndex(-1);
      },
    });
    gsap.to('#resetBtn', {
      duration: 0.5,
      ease: 'power4.Out',
      opacity: 0,
    });
  };
  return (
    <div
      className={Style.wrapper}
      onPointerUp={() => {
        if (markVisible) {
          SetMarkVisible(false);
          markInitAnim();
          if (drag) setDrag(false);
        }
      }}
    >
      <div id='canvas' className='relative w-screen h-screen'>
        <Suspense fallback={<Loader />}>
          <Canvas
            dpr={[1, 2]}
            onPointerDown={() => {
              if (!markVisible) {
                SetMarkVisible(true);
                markAnim();
              }
            }}
          >
            <ContextBridge>
              <ambientLight intensity={0.4} />
              <Model orbit={orbit} />
              <MarkerParent
                name={''}
                key={gender ? 0 : 1}
                position={[0.138, 16.88, 0.13]}
                status={zoneIndex !== -1 ? false : true}
                // orbit={orbit}
                oclusionObj={myMesh as any}
                setButtonAction={() => {
                  setZoneIndex(0);
                  popupInitAnim();
                  cameraMove({ pos: [0.138, 15.5, 0.13] });
                  canvaseMoveRight();
                }}
              />
              <MarkerParent
                name={''}
                key={gender ? 2 : 3}
                position={[0.138, 13.616, 0.128]}
                status={zoneIndex !== -1 ? false : true}
                //  orbit={orbit}
                oclusionObj={myMesh as any}
                colorIndex={1}
                setButtonAction={() => {
                  setZoneIndex(1);
                  popupInitAnim();
                  cameraMove({ pos: [0.138, 13.616, 1] });
                  canvaseMoveRight();
                }}
              />
              <MarkerParent
                name={''}
                key={gender ? 4 : 5}
                position={[0.138, 10.5, 0.128]}
                status={zoneIndex !== -1 ? false : true}
                // orbit={orbit}
                oclusionObj={myMesh as any}
                colorIndex={2}
                setButtonAction={() => {
                  setZoneIndex(2);
                  cameraMove({ pos: [0.138, 10.5, 0.128] });
                  canvaseMoveRight();
                  popupInitAnim();
                }}
              />
              <MarkerParent
                name={''}
                key={gender ? 6 : 7}
                position={[0.138, 9, 0.128]}
                status={zoneIndex !== -1 ? false : true}
                // orbit={orbit}
                oclusionObj={myMesh as any}
                colorIndex={3}
                setButtonAction={() => {
                  setZoneIndex(3);
                  cameraMove({ pos: [0.138, 9, 0.128] });
                  canvaseMoveRight();
                  popupInitAnim();
                }}
              />

              {!gender
                ? [...zoneA, ...zoneB, ...zoneC, ...zoneD].map((com, index) => {
                    let flag = false;
                    const size1 = zoneA.length;
                    const size2 = size1 + zoneB.length;
                    const size3 = size2 + zoneC.length;
                    const size4 = size3 + zoneD.length;
                    let colorindex = 0;
                    if (index < size1 && zoneIndex === 0) {
                      flag = true;
                      colorindex = 0;
                    } else if (
                      size2 > index &&
                      index >= size1 &&
                      zoneIndex === 1
                    ) {
                      flag = true;
                      colorindex = 1;
                    } else if (
                      size3 > index &&
                      index >= size2 &&
                      zoneIndex === 2
                    ) {
                      flag = true;
                      colorindex = 2;
                    } else if (
                      size4 > index &&
                      index >= size3 &&
                      zoneIndex === 3
                    ) {
                      flag = true;
                      colorindex = 3;
                    } else flag = false;
                    return (
                      <Marker
                        key={index}
                        name={com.name}
                        position={com.pos}
                        status={flag}
                        openStatu={sindex === index ? true : false}
                        //  orbit={orbit}
                        oclusionObj={myMesh as any}
                        colorIndex={colorindex}
                        setButtonAction={(e) => {
                          cameraMove({ pos: com.pos });
                          setIndex(index);
                          e.preventDefault();
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                          popupAnim(index);
                        }}
                      />
                    );
                  })
                : [...zoneA, ...zoneBF, ...zoneCF, ...zoneDF].map(
                    (com, index) => {
                      let flag = false;
                      const size1 = zoneA.length;
                      const size2 = size1 + zoneBF.length;
                      const size3 = size2 + zoneCF.length;
                      const size4 = size3 + zoneDF.length;
                      let colorindex = 0;
                      if (index < size1 && zoneIndex === 0) {
                        flag = true;
                        colorindex = 0;
                      } else if (
                        size2 > index &&
                        index >= size1 &&
                        zoneIndex === 1
                      ) {
                        flag = true;
                        colorindex = 1;
                      } else if (
                        size3 > index &&
                        index >= size2 &&
                        zoneIndex === 2
                      ) {
                        flag = true;
                        colorindex = 2;
                      } else if (
                        size4 > index &&
                        index >= size3 &&
                        zoneIndex === 3
                      ) {
                        flag = true;
                        colorindex = 3;
                      } else flag = false;
                      return (
                        <Marker
                          key={index}
                          name={com.name}
                          position={com.pos}
                          status={flag}
                          openStatu={sindex === index ? true : false}
                          //  orbit={orbit}
                          oclusionObj={myMesh as any}
                          colorIndex={colorindex}
                          setButtonAction={(e) => {
                            cameraMove({ pos: com.pos });
                            setIndex(index);
                            e.preventDefault();
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                            popupAnim(index);
                          }}
                        />
                      );
                    }
                  )}

              <mesh
                scale={[2, 15, 2.8]}
                position={[-0.2, 10, 0]}
                onPointerDown={() => {
                  if (!drag) setDrag(true);
                }}
                visible={false}
              >
                <boxGeometry />
                <meshPhongMaterial color='royalblue' transparent opacity={0} />
              </mesh>
              <PerspectiveCamera
                makeDefault
                ref={camera}
                position={[50, 0, 0]}
                zoom={window.innerWidth > 640 ? 2 : 1}
                fov={40}
              />
              <OrbitControls
                ref={orbit}
                target={[0, 11, 0]}
                enableRotate={!drag}
                minDistance={7}
                maxDistance={80}
                minPolarAngle={0}
                maxPolarAngle={Math.PI / 1.75}
                touches={{
                  ONE: THREE.TOUCH.ROTATE,
                  TWO: THREE.TOUCH.DOLLY_PAN,
                }}
              />
            </ContextBridge>
          </Canvas>
        </Suspense>
        <img
          id={'controlBtn'}
          className='absolute top-6 right-6 md:top-16 md:right-16 w-16 h-16 md:w-20 md:h-20 select-none'
          src={logo}
        />
        <button
          id='resetBtn'
          className='absolute bottom-6 right-6 md:bottom-20 md:right-16 w-10 h-10 rounded-full drop-shadow-sm shadow-md bg-white opacity-0 z-50'
          onClick={cameraRest}
        >
          <img className='p-2' src={controlBtn} />
        </button>
      </div>
      <div
        className='absolute bottom-12 left-2 md:left-16 text-xs md:text-sm font-medium flex flex-col space-y-4 items-center'
        style={{
          visibility:
            window.innerWidth <= 768 && zoneIndex > 0 ? 'hidden' : 'visible',
        }}
      >
        <p className='font-bold'>Top 5 Cancers</p>
        {!gender
          ? buttonListM.map((com, index) => (
              <button
                key={index}
                className={'flex flex-col items-center justify-center'}
                onClick={(e) => {
                  if (active) return;
                  setZoneIndex(com.zone);
                  cameraMove({ pos: com.pos });
                  setIndex(com.id);
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  popupAnim(com.id);
                }}
              >
                <div className='rounded-full bg-primaryColor hover:bg-secondaryColor'>
                  <img src={com.img} className={'md:w-12 w-8 h-auto'} />
                </div>
                <p>{com.name}</p>
              </button>
            ))
          : buttonListF.map((com, index) => (
              <button
                key={index}
                className={'flex flex-col items-center justify-center'}
                onClick={(e) => {
                  setZoneIndex(com.zone);
                  cameraMove({ pos: com.pos });
                  setIndex(com.id);
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  popupAnim(com.id);
                }}
              >
                <div className='rounded-full bg-primaryColor hover:bg-secondaryColor'>
                  <img src={com.img} className={'md:w-12 w-8 h-auto'} />
                </div>
                <p>{com.name}</p>
              </button>
            ))}
      </div>
      <Popup />
      <div className='absolute top-10 left-10 md:top-16 md:left-16'>
        <ToggleButton enabled={gender} onChange={setGender} />
      </div>
    </div>
  );
}

function Loader() {
  return (
    <div className='text-2xl font-OS_SB p-12 text-gray-500 h-screen w-screen flex justify-center items-center text-center'>
      <div className='relative'>
        <PuffLoader color='#36d7b7' size={300} />
        <p className='absolute left-24 top-36'>Loading...</p>
      </div>
    </div>
  );
}
